/**
 * Invio dei modelli (template) approvati sui numeri "Ufficiale".
 *
 * Perche' esiste (26/09/2026). Prima:
 *  - le variabili dell'intestazione e del corpo finivano tutte nel corpo;
 *  - i modelli con variabili a nome (`{{nome_cliente}}`, Meta "parameter_name")
 *    non mostravano nessun campo e partivano senza valori;
 *  - un modello con immagine/video/documento nell'intestazione partiva senza
 *    allegato e Meta lo rifiutava;
 *  - i pulsanti link con parte variabile partivano senza valore.
 *
 * Chiavi delle variabili (Record<string,string>, compatibile con
 * SendTemplateOptions.variables):
 *   'header.<nome>'           variabile dell'intestazione testuale
 *   'body.<nome>'             variabile del corpo
 *   'button.<indice>.<nome>'  parte variabile di un pulsante link
 *   'header.media_id'         id Meta di un allegato caricato per l'intestazione
 *   'header.media_link'       oppure un link https pubblico all'allegato
 * `<nome>` e' il numero (`1`, `2`) per i modelli a posizione, il nome per quelli
 * a nome. Una chiave senza prefisso (`'1'`) vale per corpo e intestazione:
 * e' il formato usato finora, resta valido.
 *
 * Formato di invio verificato sulla documentazione Meta (Template, settembre 2026):
 *   components: [
 *     { type:'header', parameters:[{ type:'image', image:{ id | link } }] },
 *     { type:'body', parameters:[{ type:'text', parameter_name?:'nome', text }] },
 *     { type:'button', sub_type:'url', index:'0', parameters:[{ type:'text', text }] },
 *   ]
 *
 * Modulo puro, testato in __tests__/templateSend.test.ts.
 */

export type TemplateHeaderFormat = 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION' | null;

export interface TemplateField {
  /** Chiave nella mappa delle variabili. */
  key: string;
  component: 'header' | 'body' | 'button';
  /** Numero o nome del segnaposto. */
  name: string;
  buttonIndex?: number;
  /** Etichetta per l'operatore. */
  label: string;
}

export interface TemplateShape {
  headerFormat: TemplateHeaderFormat;
  headerText: string;
  bodyText: string;
  footerText: string;
  buttons: Array<{ index: number; type: string; text: string; url?: string }>;
  fields: TemplateField[];
}

export const HEADER_MEDIA_ID_KEY = 'header.media_id';
export const HEADER_MEDIA_LINK_KEY = 'header.media_link';

const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj => (v && typeof v === 'object' ? (v as Obj) : {});
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** Segnaposto distinti nell'ordine in cui compaiono (`{{1}}` o `{{nome}}`). */
export function extractPlaceholders(text: string): string[] {
  if (!text) return [];
  const out: string[] = [];
  const re = new RegExp(PLACEHOLDER.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) if (!out.includes(m[1])) out.push(m[1]);
  return out;
}

const isPositional = (name: string) => /^\d+$/.test(name);

/** Ordine dei parametri per Meta: numerico per i modelli a posizione, di comparsa per quelli a nome. */
function orderNames(names: string[]): string[] {
  return names.every(isPositional) ? [...names].sort((a, b) => Number(a) - Number(b)) : names;
}

export function describeTemplate(components: unknown): TemplateShape {
  const shape: TemplateShape = { headerFormat: null, headerText: '', bodyText: '', footerText: '', buttons: [], fields: [] };
  if (!Array.isArray(components)) return shape;
  for (const c of components) {
    const comp = obj(c);
    const type = str(comp.type).toUpperCase();
    if (type === 'HEADER') {
      const format = (str(comp.format) || 'TEXT').toUpperCase();
      shape.headerFormat = (['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT', 'LOCATION'].includes(format) ? format : 'TEXT') as TemplateHeaderFormat;
      if (shape.headerFormat === 'TEXT') shape.headerText = str(comp.text);
    } else if (type === 'BODY') {
      shape.bodyText = str(comp.text);
    } else if (type === 'FOOTER') {
      shape.footerText = str(comp.text);
    } else if (type === 'BUTTONS' && Array.isArray(comp.buttons)) {
      comp.buttons.forEach((b, index) => {
        const btn = obj(b);
        shape.buttons.push({ index, type: str(btn.type).toUpperCase(), text: str(btn.text), url: str(btn.url) || undefined });
      });
    }
  }
  for (const name of orderNames(extractPlaceholders(shape.headerText))) {
    shape.fields.push({ key: `header.${name}`, component: 'header', name, label: `Intestazione {{${name}}}` });
  }
  for (const name of orderNames(extractPlaceholders(shape.bodyText))) {
    shape.fields.push({ key: `body.${name}`, component: 'body', name, label: `{{${name}}}` });
  }
  for (const b of shape.buttons) {
    if (b.type !== 'URL' || !b.url) continue;
    for (const name of extractPlaceholders(b.url)) {
      shape.fields.push({
        key: `button.${b.index}.${name}`,
        component: 'button',
        name,
        buttonIndex: b.index,
        label: `Link del pulsante "${b.text || b.index + 1}"`,
      });
    }
  }
  return shape;
}

/** Valore di un campo, con la compatibilita' per le chiavi senza prefisso. */
export function fieldValue(vars: Record<string, string> | undefined, field: TemplateField): string {
  const v = vars?.[field.key] ?? (field.component !== 'button' ? vars?.[field.name] : undefined);
  return typeof v === 'string' ? v.trim() : '';
}

export function missingFields(shape: TemplateShape, vars: Record<string, string> | undefined): TemplateField[] {
  return shape.fields.filter((f) => !fieldValue(vars, f));
}

export function headerNeedsMedia(shape: TemplateShape): boolean {
  return shape.headerFormat === 'IMAGE' || shape.headerFormat === 'VIDEO' || shape.headerFormat === 'DOCUMENT';
}

export function hasHeaderMedia(vars: Record<string, string> | undefined): boolean {
  return !!vars?.[HEADER_MEDIA_ID_KEY]?.trim() || /^https:\/\//.test(vars?.[HEADER_MEDIA_LINK_KEY]?.trim() ?? '');
}

/**
 * Perche' il modello non puo' ancora partire, in italiano, oppure null se e'
 * pronto. Usato sia dal selettore (pulsante Invia) sia dal provider prima di
 * chiamare Meta.
 */
export function templateBlockReason(shape: TemplateShape, vars: Record<string, string> | undefined): string | null {
  if (shape.headerFormat === 'LOCATION') {
    return "Questo modello ha una posizione nell'intestazione: non si puo' ancora inviare dalla chat.";
  }
  if (headerNeedsMedia(shape) && !hasHeaderMedia(vars)) {
    const what = shape.headerFormat === 'IMAGE' ? "un'immagine" : shape.headerFormat === 'VIDEO' ? 'un video' : 'un documento';
    return `Questo modello ha ${what} nell'intestazione: allegalo prima di inviare.`;
  }
  const missing = missingFields(shape, vars);
  if (missing.length > 0) {
    return missing.length === 1 ? 'Manca 1 variabile da compilare.' : `Mancano ${missing.length} variabili da compilare.`;
  }
  return null;
}

const textParam = (name: string, text: string) =>
  isPositional(name) ? { type: 'text', text } : { type: 'text', parameter_name: name, text };

/** Il campo `components` da mandare a Meta. Chiamare dopo templateBlockReason() === null. */
export function buildTemplateSendComponents(shape: TemplateShape, vars: Record<string, string> | undefined): unknown[] {
  const out: unknown[] = [];
  const byComponent = (c: TemplateField['component']) => shape.fields.filter((f) => f.component === c);

  if (headerNeedsMedia(shape) && hasHeaderMedia(vars)) {
    const kind = shape.headerFormat!.toLowerCase();
    const id = vars?.[HEADER_MEDIA_ID_KEY]?.trim();
    const media = id ? { id } : { link: vars![HEADER_MEDIA_LINK_KEY]!.trim() };
    out.push({ type: 'header', parameters: [{ type: kind, [kind]: media }] });
  } else {
    const header = byComponent('header');
    if (header.length > 0) out.push({ type: 'header', parameters: header.map((f) => textParam(f.name, fieldValue(vars, f))) });
  }

  const body = byComponent('body');
  if (body.length > 0) out.push({ type: 'body', parameters: body.map((f) => textParam(f.name, fieldValue(vars, f))) });

  const buttons = new Map<number, TemplateField[]>();
  for (const f of byComponent('button')) buttons.set(f.buttonIndex!, [...(buttons.get(f.buttonIndex!) ?? []), f]);
  for (const [index, fields] of buttons) {
    out.push({
      type: 'button',
      sub_type: 'url',
      index: String(index),
      parameters: fields.map((f) => ({ type: 'text', text: fieldValue(vars, f) })),
    });
  }
  return out;
}

function fill(text: string, component: 'header' | 'body', vars: Record<string, string> | undefined): string {
  return text.replace(PLACEHOLDER, (match, name: string) => {
    const v = fieldValue(vars, { key: `${component}.${name}`, component, name, label: '' });
    return v || match;
  });
}

/** Testo che legge il cliente (intestazione, corpo, piede), da salvare in chat. */
export function renderTemplatePlainText(shape: TemplateShape, vars: Record<string, string> | undefined): string {
  return [fill(shape.headerText, 'header', vars), fill(shape.bodyText, 'body', vars), shape.footerText]
    .filter((t) => t.trim())
    .join('\n\n');
}
